import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Moon, Sun, Globe } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import { DE_TO_EN, EN_TO_DE, isEnglishPath } from '../utils/i18nRoutes';
import TopInfoBar from './TopInfoBar';

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { theme, toggleTheme } = useTheme();
  const { language, setLanguage } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();

  const currentPath = location.pathname.replace(/\/$/, '') || '/';
  const onEnPage = isEnglishPath(currentPath);
  const isHomePage = currentPath === '/' || currentPath === '/en';

  const switchLanguage = () => {
    const target = onEnPage
      ? (EN_TO_DE[currentPath] || '/')
      : (DE_TO_EN[currentPath] || '/en/');
    setLanguage(onEnPage ? 'de' : 'en');
    navigate(target);
    setIsMobileMenuOpen(false);
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  const navItems = onEnPage
    ? [
        { label: 'Services', to: '/en/services' },
        { label: 'About', to: '/en/about-us' },
        { label: 'References', to: '/en/references' },
        { label: 'Contact', to: '/en/contact' },
      ]
    : [
        { label: 'Leistungen', to: '/leistungen' },
        { label: 'Über uns', to: '/ueber-uns' },
        { label: 'Referenzen', to: '/referenzen' },
        { label: 'Kontakt', to: '/kontakt' },
      ];

  const ctaLabel = onEnPage ? 'Free consultation' : 'Kostenloses Erstgespräch';
  const ctaLink = isHomePage ? '#anfrage' : (onEnPage ? '/en/contact' : '/kontakt');

  const themeLabelDark = language === 'de' ? 'Dunkel' : 'Dark';
  const themeLabelLight = language === 'de' ? 'Hell' : 'Light';

  return (
    <>
      <div className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled || isMobileMenuOpen
          ? 'bg-white dark:bg-gray-900 shadow-md'
          : 'bg-white dark:bg-gray-900'
      }`}>
        <TopInfoBar />
        <div className="max-w-7xl mx-auto px-6 py-3">
          <div className="flex items-center justify-between">
            <a href="/" className="flex items-center gap-3 group" aria-label="OptiAI Home">
              <picture>
                <source srcSet="/OptiAI_logo_transparent.webp" type="image/webp" />
                <img
                  src="/OptiAI_logo_transparent.png"
                  alt="OptiAI – KI-Integration & Kostenoptimierung"
                  width="160"
                  height="40"
                  loading="eager"
                  decoding="async"
                  className="h-9 w-auto"
                />
              </picture>
            </a>

            <nav className="hidden md:flex items-center gap-6">
              {navItems.map((item) => (
                <Link
                  key={item.label}
                  to={item.to}
                  className="text-gray-600 dark:text-gray-300 hover:text-primary dark:hover:text-accent-light font-medium transition-colors"
                >
                  {item.label}
                </Link>
              ))}

              <div className="flex items-center gap-2 border-l border-line dark:border-gray-700 pl-6">
                <button
                  onClick={toggleTheme}
                  className="p-2 text-gray-600 dark:text-gray-300 hover:text-primary dark:hover:text-accent-light rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  aria-label="Toggle theme"
                >
                  {theme === 'light' ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
                </button>

                <button
                  onClick={switchLanguage}
                  className="flex items-center gap-1 px-3 py-2 text-gray-600 dark:text-gray-300 hover:text-primary dark:hover:text-accent-light rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors font-medium"
                  aria-label="Toggle language"
                >
                  <Globe className="w-4 h-4" />
                  <span className="text-sm font-bold">{onEnPage ? 'DE' : 'EN'}</span>
                </button>
              </div>

              <a
                href={ctaLink}
                className="btn-primary text-sm"
              >
                {ctaLabel}
              </a>
            </nav>

            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 text-dark dark:text-white transition-colors"
              aria-label={isMobileMenuOpen ? (language === 'de' ? 'Menü schließen' : 'Close menu') : (language === 'de' ? 'Menü öffnen' : 'Open menu')}
              aria-expanded={isMobileMenuOpen}
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="md:hidden fixed inset-0 z-40 bg-white dark:bg-gray-900 flex flex-col pt-20">
          <nav className="flex flex-col px-6 py-6 gap-1 flex-1 overflow-y-auto">
            {navItems.map((item) => (
              <Link
                key={item.label}
                to={item.to}
                onClick={() => setIsMobileMenuOpen(false)}
                className="text-gray-700 dark:text-gray-200 hover:text-primary dark:hover:text-accent-light font-semibold text-lg py-4 border-b border-gray-100 dark:border-gray-800 transition-colors"
              >
                {item.label}
              </Link>
            ))}

            <div className="flex items-center gap-3 pt-6">
              <button
                onClick={() => {
                  toggleTheme();
                }}
                className="flex-1 flex items-center justify-center gap-2 p-3 text-gray-700 dark:text-gray-200 rounded-full bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors font-semibold"
                aria-label="Toggle theme"
              >
                {theme === 'light' ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
                <span>{theme === 'light' ? themeLabelDark : themeLabelLight}</span>
              </button>

              <button
                onClick={switchLanguage}
                className="flex-1 flex items-center justify-center gap-2 p-3 text-gray-700 dark:text-gray-200 rounded-full bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors font-semibold"
                aria-label="Toggle language"
              >
                <Globe className="w-5 h-5" />
                <span className="font-bold">{onEnPage ? 'DE' : 'EN'}</span>
              </button>
            </div>

            <a
              href={ctaLink}
              onClick={() => setIsMobileMenuOpen(false)}
              className="mt-4 btn-primary text-center text-lg py-4"
            >
              {ctaLabel}
            </a>
          </nav>
        </div>
      )}
    </>
  );
}
